import React, { createContext, useContext, useCallback, useMemo, useState, useRef } from 'react';

/**
 * Window manager for the advanced item tooltip.
 *
 * Interaction model (reconciled with #183 + user feedback):
 *  - hover an id → a lightweight `mini` peek (read-only, one at a time).
 *  - move the pointer INTO the mini → it expands to the full card as an
 *    *unpinned preview* window (dismiss-on-leave).
 *  - pin it (📌 / start editing) → a *pinned* window: draggable on desktop, a
 *    modal on mobile, persistent, and recursive (multiple can be open).
 *  - clicking the id itself is a hyperlink (navigation) — handled by the trigger,
 *    not here.
 */
const ItemCardContext = createContext(null);

const noop = () => { /* default no-op: no ItemCardProvider mounted */ };

export function useItemCards() {
  const ctx = useContext(ItemCardContext);
  return ctx || {
    mini: null, windows: [], navigate: noop,
    showMini: noop, hideMini: noop, cancelHide: noop,
    openWindow: noop, closeWindow: noop, pinWindow: noop,
    focusWindow: noop, moveWindow: noop, resizeWindow: noop,
  };
}

const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 640;

export function ItemCardProvider({ children, navigate = noop }) {
  const [mini, setMini] = useState(null);     // { item, rect }
  const [windows, setWindows] = useState([]); // [{ key, item, x, y, z, pinned, rect }]
  const zRef = useRef(10);
  const hideTimer = useRef(null);

  const showMini = useCallback((item, rect) => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
    // Bump above any already-open pinned windows so the mini (and, via it, the
    // full card it can expand into) is never rendered underneath one of them.
    const z = ++zRef.current;
    setMini({ item, rect, z });
  }, []);

  const hideMini = useCallback((immediate = false) => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (immediate) { setMini(null); return; }
    hideTimer.current = setTimeout(() => setMini(null), 140);
  }, []);

  // #801: leaving the trigger schedules the hide 140ms out, and moving INTO the mini
  // arms its expand 100ms out — so the pointer arriving at the mini normally wins by
  // 40ms. Under load the expand callback slides and the hide fires first, tearing the
  // peek away from under a pointer that is resting on it. Arriving cancels the hide
  // outright rather than relying on that margin; observed in a trace as `.ic-mini`
  // going visible and then vanishing before it could be hovered.
  const cancelHide = useCallback(() => {
    if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; }
  }, []);

  const focusWindow = useCallback((key) => {
    setWindows((ws) => {
      const maxZ = ws.reduce((m, w) => Math.max(m, w.z), 0);
      const target = ws.find((w) => w.key === key);
      if (!target || target.z === maxZ) return ws;
      const z = ++zRef.current;
      return ws.map((w) => (w.key === key ? { ...w, z } : w));
    });
  }, []);

  const openWindow = useCallback((item, rect, opts = {}) => {
    const pinned = !!opts.pinned;
    setMini(null);
    const id = item?.id || item?.display_id;
    if (!id) return;
    setWindows((ws) => {
      // Unpinned preview is singular: drop any existing preview first.
      const base = pinned ? ws : ws.filter((w) => w.pinned);
      const existing = base.find((w) => (w.item.id || w.item.display_id) === id);
      const z = ++zRef.current;
      if (existing) return base.map((w) => (w === existing ? { ...w, z, pinned: w.pinned || pinned } : w));
      const n = base.length;
      const EST_W = 460, EST_H = 460, M = 8;
      let x = rect ? rect.left : 80;
      let y = rect ? rect.bottom + 6 : 80;
      if (isMobile()) { x = M; y = M; }
      x = Math.max(M, Math.min(x + (pinned ? n * 24 : 0), window.innerWidth - EST_W - M));
      y = Math.max(M, Math.min(y + (pinned ? n * 24 : 0), window.innerHeight - EST_H - M));
      return [...base, { key: `${id}#${z}`, item, x, y, z, pinned, rect }];
    });
  }, []);

  const pinWindow = useCallback((key) => {
    setWindows((ws) => ws.map((w) => (w.key === key ? { ...w, pinned: true } : w)));
  }, []);

  const closeWindow = useCallback((key) => {
    setWindows((ws) => ws.filter((w) => w.key !== key));
  }, []);

  const moveWindow = useCallback((key, x, y) => {
    setWindows((ws) => ws.map((w) => (w.key === key ? { ...w, x, y } : w)));
  }, []);

  // Resize (any edge/corner). `patch` may carry x/y too — resizing from the
  // top/left edge moves the origin to keep the opposite corner anchored.
  const resizeWindow = useCallback((key, patch) => {
    setWindows((ws) => ws.map((w) => (w.key === key ? { ...w, ...patch } : w)));
  }, []);

  // useMemo: a fresh object here re-renders every consumer on every provider render,
  // and this provider re-renders whenever `windows` changes — which is on every drag
  // frame (#943, react/jsx-no-constructed-context-values).
  const value = useMemo(() => ({
    mini, windows, navigate, showMini, hideMini, cancelHide, openWindow, closeWindow, pinWindow,
    focusWindow, moveWindow, resizeWindow,
  }), [mini, windows, navigate, showMini, hideMini, cancelHide, openWindow, closeWindow,
    pinWindow, focusWindow, moveWindow, resizeWindow]);
  return <ItemCardContext.Provider value={value}>{children}</ItemCardContext.Provider>;
}

export default ItemCardContext;
